import React, { useState } from 'react';
import {
  Box,
  CardContent,
  Button,
  Typography,
  InputAdornment,
  styled,
  alpha,
} from '@mui/material';
import { Email, Send, ArrowBack } from '@mui/icons-material';
import Logo from '../common/Logo';
import { authApi } from '../../services/api/authApi';
import ccLogo from '../../assets/images/cclogo.svg';
import { StyledCard, StyledTextField, AuthErrorAlert } from './_shared';

// Same email shape LoginForm validates against.
const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// Gradient submit button mirroring LoginForm's "Sign in" button.
const ResetButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    background: theme.palette.grey[300],
  },
}));

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState('');

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (validationError) setValidationError('');
    if (error) setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!email) {
      setValidationError('Email is required');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Failed to send reset email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 5 }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Logo src={ccLogo} width={280} style={{ marginTop: 15, marginBottom: 15 }} />
        </Box>

        {success ? (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
              }}
            >
              <Email sx={{ fontSize: 40, color: 'success.main' }} />
            </Box>

            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: 'grey.800', mb: 2 }}>
              Check Your Email
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              We've sent password reset instructions to:
            </Typography>

            <Typography variant="body1" sx={{ fontWeight: 600, mt: 1, mb: 3 }}>
              {email}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              If you don't receive an email within a few minutes, please check your spam folder.
            </Typography>

            <ResetButton fullWidth variant="contained" onClick={onBackToLogin} startIcon={<ArrowBack />}>
              Back to Login
            </ResetButton>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Typography
                variant="h5"
                component="h1"
                sx={{ fontWeight: 700, color: 'grey.800', letterSpacing: '-0.01em', mb: 1 }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '0.95rem' }}
              >
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
            </Box>

            <AuthErrorAlert message={error} />

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <StyledTextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={!!validationError}
                helperText={validationError}
                disabled={loading}
                margin="normal"
                placeholder="name@company.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'grey.500', fontSize: '1.25rem' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <ResetButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                endIcon={!loading && <Send />}
              >
                {loading ? 'Sending...' : 'Reset Password'}
              </ResetButton>
            </Box>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ForgotPasswordForm;
